import { type MeetingRuleDraft, type TeamMember } from './team.types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

const asDomain = (text: string) => text.replace(/^@/, '');

export const looksLikeDomain = (raw: string) =>
  DOMAIN_PATTERN.test(asDomain(raw.trim().toLowerCase()));

/** One input for all three rule kinds: an email, a domain, or else a title keyword */
export const parseMeetingRuleInput = (
  raw: string,
  matchTitle: boolean
): MeetingRuleDraft | null => {
  const text = raw.trim().toLowerCase();
  if (!text) return null;
  if (EMAIL_PATTERN.test(text)) return { kind: 'attendee_email', value: text };
  if (!matchTitle && looksLikeDomain(text)) {
    return { kind: 'attendee_domain', value: asDomain(text) };
  }
  return { kind: 'title_keyword', value: text };
};

export const describeMeetingRule = ({ kind, value }: MeetingRuleDraft) => {
  switch (kind) {
    case 'title_keyword':
      return `Skips meetings whose title contains "${value}".`;
    case 'attendee_email':
      return `Skips meetings that ${value} is invited to.`;
    case 'attendee_domain':
      return `Skips meetings with anyone from ${value} or its subdomains.`;
  }
};

export const meetingRuleLabel = ({ kind, value }: MeetingRuleDraft) => {
  switch (kind) {
    case 'title_keyword':
      return `"${value}"`;
    case 'attendee_email':
      return value;
    case 'attendee_domain':
      return `anyone from ${value}`;
  }
};

/** Who added a rule, shown only when it was a teammate rather than the viewer */
export const ruleAttribution = (
  addedBy: string | null,
  members: TeamMember[],
  currentUserEmail: string | undefined
) => {
  if (!addedBy || addedBy === currentUserEmail) return null;
  const name = members.find((member) => member.email === addedBy)?.name;
  if (!name) return null;
  return {
    short: `by ${name.split(' ')[0]}`,
    full: `Added by ${name} (${addedBy})`,
  };
};
